import type { OnyxCustomAddress, OnyxTime } from '@onyx/angular';
import { groupBy, minBy, orderBy, uniq } from 'lodash';
import type { DateTime } from 'luxon';
import { DelegationStatus } from '../../../dashboard/delegations/common/enums/delegation-status';
import { TechnicalDelegationPointName } from '../../../dashboard/fleet/common/enums/technical-delegation-point-name';
import { TechnicalPointCategory } from '../../../dashboard/fleet/common/enums/technical-point-category';
import { DelegationPointType } from '../../../dashboard/fleet/common/enums/technical-point-type';
import type { DelegationPoint } from '../../../dashboard/fleet/common/interfaces/technical-point';
import { OrderPointStatus } from '../../../dashboard/orders/common/enums/order-point-status';
import { OrderTimeWindowType } from '../../../dashboard/orders/common/enums/order-time-window-type';
import { RouteHelper } from '../../helpers/route.helper';
import type { TelematicsDelegation } from '../../interfaces/telematics/telematics-delegation';
import type { TelematicsRouteData } from '../../interfaces/telematics/telematics-event';
import type {
  TelematicsRoutePoint,
  TelematicsRoutePointDto,
} from '../../interfaces/telematics/telematics-route-point';
import { TelematicsRoutePointStatus } from '../../interfaces/telematics/telematics-vehicle-point-status';

interface DelegationCycle {
  points: TelematicsRoutePointDto[];
  delegations: Map<string, TelematicsDelegation>;
}

export class TelematicsRoutePointFactory {
  public static from(data: {
    route: TelematicsRouteData;
    delegations?: TelematicsDelegation[];
  }): TelematicsRoutePoint[] {
    return this.fromDto(data.route.route ?? [], data.delegations);
  }

  public static fromDto(
    dto: TelematicsRoutePointDto[],
    delegations?: TelematicsDelegation[],
  ): TelematicsRoutePoint[] {
    if (!delegations?.length) return this.fromDtoWithoutDelegations(dto);

    const delegationsByUuid = new Map(
      delegations.map((delegation) => [delegation.delegation.uuid, delegation]),
    );
    const cycles = this.splitIntoCycles(dto, delegationsByUuid);
    const cyclesWithoutPoints = this.getCyclesWithoutPoints(
      cycles,
      delegationsByUuid,
    );

    const firstCycle = cycles.at(0);
    if (firstCycle && !firstCycle.delegations.size) {
      firstCycle.delegations =
        cyclesWithoutPoints.shift()?.delegations ?? new Map();
    }

    for (const cycle of cyclesWithoutPoints) {
      const cycleStart = this.getCycleStart(cycle)!;
      const previousCycleIndex = cycles.findLastIndex((routeCycle) => {
        const routeCycleStart = this.getCycleStart(routeCycle);
        return !!routeCycleStart && routeCycleStart <= cycleStart;
      });
      cycles.splice(previousCycleIndex + 1, 0, cycle);
    }

    return cycles.flatMap((cycle) => this.buildCycle(cycle, delegationsByUuid));
  }

  private static splitIntoCycles(
    dto: TelematicsRoutePointDto[],
    delegationsByUuid: ReadonlyMap<string, TelematicsDelegation>,
  ): DelegationCycle[] {
    const cycles: DelegationCycle[] = [];

    for (const point of dto) {
      const pointDelegations = this.getKnownDelegations(
        point,
        delegationsByUuid,
      );
      const cycle = cycles.at(-1);

      if (
        !cycle ||
        (pointDelegations.length &&
          cycle.delegations.size &&
          !pointDelegations.some(({ delegation }) =>
            cycle.delegations.has(delegation.uuid),
          ))
      ) {
        cycles.push({ points: [point], delegations: new Map() });
      } else {
        cycle.points.push(point);
      }

      for (const delegation of pointDelegations) {
        cycles.at(-1)!.delegations.set(delegation.delegation.uuid, delegation);
      }
    }

    return cycles;
  }

  private static getCyclesWithoutPoints(
    routeCycles: DelegationCycle[],
    delegationsByUuid: ReadonlyMap<string, TelematicsDelegation>,
  ): DelegationCycle[] {
    const routeDelegationUuids = new Set(
      routeCycles.flatMap(({ delegations }) => [...delegations.keys()]),
    );
    const firstRouteCycleStart = routeCycles.length
      ? this.getCycleStart(routeCycles[0])
      : undefined;
    const delegationsWithoutPoints = [...delegationsByUuid.values()].filter(
      ({ delegation, start }) =>
        !routeDelegationUuids.has(delegation.uuid) &&
        !(firstRouteCycleStart && start < firstRouteCycleStart),
    );

    return orderBy(
      this.groupByIdentity(delegationsWithoutPoints),
      (group) => group[0].start,
      'asc',
    ).map((group) => ({
      points: [],
      delegations: new Map(
        group.map((delegation) => [delegation.delegation.uuid, delegation]),
      ),
    }));
  }

  private static buildCycle(
    { points, delegations }: DelegationCycle,
    delegationsByUuid: ReadonlyMap<string, TelematicsDelegation>,
  ): TelematicsRoutePoint[] {
    const result: TelematicsRoutePoint[] = [];
    const activeDelegations = new Map<string, TelematicsDelegation>();
    let cycleIndex = 0;

    const delegationsByPoint = points.map((point) =>
      this.getKnownDelegations(point, delegationsByUuid),
    );
    const lastPointIndexes = new Map<string, number>();
    for (const [index, pointDelegations] of delegationsByPoint.entries()) {
      for (const { delegation } of pointDelegations) {
        lastPointIndexes.set(delegation.uuid, index);
      }
    }
    const lastDelegationPointIndex = Math.max(-1, ...lastPointIndexes.values());

    const activateDelegations = (delegations: TelematicsDelegation[]) => {
      for (const delegation of delegations) {
        activeDelegations.set(delegation.delegation.uuid, delegation);
      }
    };

    const closeDelegations = (
      endingDelegations: TelematicsDelegation[],
      isCycleEnd: boolean,
      hasCycleActivePoints: boolean,
    ) => {
      const groups = orderBy(
        this.groupByIdentity(endingDelegations),
        (group) => group[0].end,
        'asc',
      );

      for (const [index, group] of groups.entries()) {
        const isCycleEndPoint = isCycleEnd && index === groups.length - 1;
        const point = this.buildDelegationPoint(
          group,
          [...activeDelegations.values()],
          DelegationPointType.END,
          cycleIndex + 1,
          {
            hasCycleActivePoints,
            isMidCycleDelegationEnd: !isCycleEnd,
            name: isCycleEndPoint
              ? undefined
              : TechnicalDelegationPointName.DELEGATION_END,
          },
        );

        for (const { delegation } of group) {
          activeDelegations.delete(delegation.uuid);
        }
        if (!point) continue;

        cycleIndex = point.cycleIndex;
        result.push(point);
      }
    };

    const startDelegations = delegationsByPoint.find(
      (pointDelegations) => pointDelegations.length,
    ) ?? [...delegations.values()];
    activateDelegations(startDelegations);

    const [startGroup] = orderBy(
      this.groupByIdentity(startDelegations),
      (group) => group[0].start,
      'asc',
    );
    if (startGroup) {
      const startPoint = this.buildDelegationPoint(
        startGroup,
        [...activeDelegations.values()],
        DelegationPointType.START,
        cycleIndex,
        { hasCycleActivePoints: false },
      );
      if (startPoint) result.push(startPoint);
    }

    for (const [index, routePoint] of points.entries()) {
      activateDelegations(delegationsByPoint[index]);

      const isTechnicalPoint = !!routePoint.technicalPoint;
      const point = {
        ...routePoint,
        order: isTechnicalPoint ? null : routePoint.order,
        point: isTechnicalPoint
          ? null
          : { ...routePoint.point, route: routePoint.route },
        technicalPoint: isTechnicalPoint
          ? { ...routePoint.technicalPoint, route: routePoint.route }
          : null,
      } as TelematicsRoutePoint;

      if (
        RouteHelper.isIncludedInOrder({
          ...(point.point ?? point.technicalPoint),
          route: point.route,
        })
      ) {
        ++cycleIndex;
      }

      point.cycleIndex = cycleIndex;
      result.push(point);

      if (index < lastDelegationPointIndex) {
        closeDelegations(
          delegationsByPoint[index].filter(
            ({ delegation }) => lastPointIndexes.get(delegation.uuid) === index,
          ),
          false,
          this.hasCycleActivePoints(points.slice(0, index + 1)),
        );
      }
    }

    closeDelegations(
      [...activeDelegations.values()],
      true,
      this.hasCycleActivePoints(points),
    );

    const cycleDelegations = [...delegations.values()].map(
      ({ delegation }) => delegation,
    );
    for (const { technicalPoint } of result) {
      if (technicalPoint?.category === TechnicalPointCategory.DELEGATION) {
        technicalPoint.delegationData.delegations = cycleDelegations;
      }
    }

    return result;
  }

  private static getCycleStart({
    delegations,
  }: DelegationCycle): DateTime | undefined {
    return minBy([...delegations.values()], ({ start }) => start)?.start;
  }

  private static getKnownDelegations(
    point: TelematicsRoutePointDto,
    delegationsByUuid: ReadonlyMap<string, TelematicsDelegation>,
  ): TelematicsDelegation[] {
    return this.getDelegationUuids(point)
      .map((uuid) => delegationsByUuid.get(uuid))
      .filter((delegation) => delegation != null);
  }

  private static groupByIdentity(
    delegations: TelematicsDelegation[],
  ): TelematicsDelegation[][] {
    return Object.values(
      groupBy(delegations, (delegation) =>
        this.getDelegationIdentityKey(delegation),
      ),
    );
  }

  private static getDelegationIdentityKey(
    telematicsDelegation: TelematicsDelegation,
  ): string {
    const start = telematicsDelegation.delegation.start;
    const end = telematicsDelegation.delegation.end;

    return `${telematicsDelegation.status}_${start.date}T${start.time}_${end?.date ?? ''}T${end?.time ?? ''}`;
  }

  private static fromDtoWithoutDelegations(
    dto: TelematicsRoutePointDto[],
  ): TelematicsRoutePoint[] {
    let cycleIndex = -1;
    let previousDelegations = new Set<string>();

    return dto.map((routePoint) => {
      const delegations = this.getDelegationUuids(routePoint);

      if (
        (delegations.length || previousDelegations.size) &&
        delegations.every((uuid) => !previousDelegations.has(uuid))
      ) {
        cycleIndex = -1;
      }
      previousDelegations = new Set(delegations);

      if (
        RouteHelper.isIncludedInOrder({
          ...(routePoint.point ?? routePoint.technicalPoint),
          route: routePoint.route,
        })
      ) {
        ++cycleIndex;
      }

      if (routePoint.order && routePoint.point) {
        const point = { ...routePoint.point, route: routePoint.route };

        return {
          ...routePoint,
          cycleIndex,
          point,
          technicalPoint: null,
        } as TelematicsRoutePoint;
      }

      const technicalPoint = {
        ...routePoint.technicalPoint,
        route: routePoint.route,
      };

      return {
        ...routePoint,
        cycleIndex,
        order: null,
        point: null,
        technicalPoint,
      } as TelematicsRoutePoint;
    });
  }

  private static getDelegationUuids(point: TelematicsRoutePointDto): string[] {
    return uniq([
      point.primaryDriverDelegationUuid,
      point.secondaryDriverDelegationUuid,
    ]).filter((uuid) => uuid != null);
  }

  private static buildDelegationPoint(
    group: TelematicsDelegation[],
    currentCycleDelegations: TelematicsDelegation[],
    type: DelegationPointType,
    cycleIndex: number,
    options: {
      hasCycleActivePoints: boolean;
      isMidCycleDelegationEnd?: boolean;
      name?: string;
    },
  ): TelematicsRoutePoint | null {
    const telematicsDelegation = group[0];
    const routePointStatus = () =>
      ({
        [DelegationStatus.PLANNED]: TelematicsRoutePointStatus.PLANNED,
        [DelegationStatus.ACTIVE]: TelematicsRoutePointStatus.ASSIGNED,
        [DelegationStatus.UNSETTLED]: TelematicsRoutePointStatus.COMPLETED,
        [DelegationStatus.SETTLED]: TelematicsRoutePointStatus.CANCELED,
        [DelegationStatus.ARCHIVED]: TelematicsRoutePointStatus.COMPLETED,
        [DelegationStatus.CANCELED]: TelematicsRoutePointStatus.CANCELED,
      })[telematicsDelegation.status];

    const delegation = telematicsDelegation.delegation;
    const address =
      type === DelegationPointType.START
        ? delegation.start.address
        : delegation.end.address;
    if (!address) return null;

    const date =
      type === DelegationPointType.START
        ? delegation.start.date
        : delegation.end?.date;
    const time =
      type === DelegationPointType.START
        ? delegation.start.time
        : delegation.end?.time;
    if (!date || !time) return null;

    return {
      cycleIndex,
      route: RouteHelper.createEmptyRoute(address.coordinates),
      order: null,
      point: null,
      status: routePointStatus(),
      deadlineTime: null,
      bufferTime: null,
      primaryDriverDelegationUuid: group[0].delegation.uuid,
      secondaryDriverDelegationUuid: group[1]?.delegation.uuid ?? null,
      trailerUuid: null,
      technicalPoint: this.createDelegationTechnicalPoint(
        group,
        currentCycleDelegations,
        type,
        address,
        date,
        time,
        options,
      ),
    };
  }

  private static createDelegationTechnicalPoint(
    group: TelematicsDelegation[],
    currentCycleDelegations: TelematicsDelegation[],
    type: DelegationPointType,
    address: OnyxCustomAddress,
    date: string,
    time: OnyxTime,
    options: {
      hasCycleActivePoints: boolean;
      isMidCycleDelegationEnd?: boolean;
      name?: string;
    },
  ): DelegationPoint {
    const telematicsDelegation = group[0];
    const { name, hasCycleActivePoints, isMidCycleDelegationEnd } =
      options ?? {};

    const status = () => {
      const isEnd = type === DelegationPointType.END;
      if (isEnd && hasCycleActivePoints) return OrderPointStatus.TO_DO;

      return {
        [DelegationStatus.PLANNED]: OrderPointStatus.TO_DO,
        [DelegationStatus.ACTIVE]: isEnd
          ? OrderPointStatus.TO_DO
          : OrderPointStatus.COMPLETED,
        [DelegationStatus.UNSETTLED]: OrderPointStatus.COMPLETED,
        [DelegationStatus.SETTLED]: OrderPointStatus.CANCELED,
        [DelegationStatus.ARCHIVED]: OrderPointStatus.COMPLETED,
        [DelegationStatus.CANCELED]: OrderPointStatus.CANCELED,
      }[telematicsDelegation.status];
    };

    return {
      uuid: `${telematicsDelegation.delegation.uuid}-${type}`,
      address,
      timeWindows: {
        type: OrderTimeWindowType.FIX,
        windows: [{ date, time, timeRange: null }],
      },
      category: TechnicalPointCategory.DELEGATION,
      type,
      name: name ?? type,
      status: status(),
      delegationData: {
        delegation: telematicsDelegation,
        currentDelegations:
          currentCycleDelegations?.map(({ delegation }) => delegation) ?? [],
        delegations: [],
        hasCycleActivePoints,
        isMidCycleDelegationEnd,
      },
      estimations: null,
      timestamps: {
        arrivedAt: `${date}T${time}`,
        canceledAt: null,
        completedAt: null,
        startedAt: null,
      },
      route: RouteHelper.createEmptyRoute(address.coordinates),
    };
  }

  private static hasCycleActivePoints(points: TelematicsRoutePointDto[]) {
    return points.some(
      (point) =>
        ![
          TelematicsRoutePointStatus.COMPLETED,
          TelematicsRoutePointStatus.CANCELED,
        ].includes(point.status),
    );
  }
}
